import { FormikProvider, getIn, useFormik } from 'formik'
import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch } from 'react-redux'
import { useHistory, useLocation } from 'react-router-dom'
import { Container, Form, TabContent, TabPane } from 'reactstrap'
import * as Yup from 'yup'
import { setPropertyStatus } from '../../../Components/AppPermissionsSwitcher/utils/propertyStatus'
import FormActionButtons from '../../../Components/Common/FormActionButtons'
import PageTitle from '../../../Components/Common/PageTitle'
import { useFormValidationErrorModal } from '../../../Components/Modals/FormValidationErrorModal/useFormValidationErrorModal'
import PropertyFormDescriptionTab, {
    PropertyFormDescriptionTabMain,
    PropertyFormDescriptionTabSide,
} from '../../../Components/PropertyFormDescriptionTab/PropertyFormDescriptionTab'
import PropertyFormGeneralTab, {
    PropertyFormGeneralTabMain,
    PropertyFormGeneralTabSide,
} from '../../../Components/PropertyFormGeneralTab/PropertyFormGeneralTab'
import auth from '../../../api/auth/hooks'
import properties from '../../../api/properties/hooks'
import { PropertyCreatePayload, PropertyCreateTranslation } from '../../../api/properties/types'
import { realEstateGeneralFields } from '../../../common/forms/generalRealEstate/fields'
import { useInitializeLocationFilters } from '../../../hooks/useInitializeLocationFilters'
import { resetFormFilters } from '../../../store/formFilters'
import { changeTranslationObject } from '../../../utils/general'
import { propertyGeneralFormTabs } from '../constants'
import { usePropertyDescriptionFormConfig, usePropertyGeneralFormConfig } from '../hooks'
import { getValidData, useFormDataExtraction } from '../utils'
import { DescriptionValidationConfig, FormikConfig } from './PropertyCreate.types'

const PropertyCreate = () => {
    const history = useHistory()
    const location = useLocation()
    const { t } = useTranslation()
    const dispatch = useDispatch()

    const [activeTab, setActiveTab] = useState('general')

    const { data: currentAgent } = auth.CurrentAgent.useQuery({
        select: (data) => ({
            defaultCountry: data.defaultCountry,
            defaultCounty: data.defaultCounty,
            defaultCity: data.defaultCity,
            agentPermissions: data.agentPermissions,
        }),
    })
    const { mutate: createProperty } = properties.CreateUpdateProperty.useMutation()

    const agentLocationData = useMemo(
        () => ({
            country: currentAgent?.defaultCountry,
            county: currentAgent?.defaultCounty,
            city: currentAgent?.defaultCity,
        }),
        [currentAgent?.defaultCountry, currentAgent?.defaultCounty, currentAgent?.defaultCity]
    )

    useInitializeLocationFilters(agentLocationData)
    const { formInputs, formikConfig } = usePropertyGeneralFormConfig(false, agentLocationData)
    const { descriptionInputs, descriptionValidationConfig } = usePropertyDescriptionFormConfig(null)

    const formik = useFormik({
        enableReinitialize: true,
        initialValues: {
            description: (descriptionValidationConfig as unknown as DescriptionValidationConfig).initialValues ?? {},
            general: (formikConfig as unknown as FormikConfig).initialValues ?? {},
        },
        validationSchema: Yup.object({
            general: (formikConfig as unknown as FormikConfig).validationSchema ?? Yup.object({}),
            description: (descriptionValidationConfig as unknown as DescriptionValidationConfig).validationSchema ?? Yup.object({}),
        }),
        onSubmit: (values) => {
            const generalValues = (formikConfig as unknown as FormikConfig).prepareModelForSubmit(values.general)
            const { defaultStatus } = setPropertyStatus({ agentPermissions: currentAgent?.agentPermissions })

            const translations = changeTranslationObject(values.description, 'property_title', 'property_description') as PropertyCreateTranslation[]

            const submitValues: PropertyCreatePayload = {
                ...generalValues,
                ...values.description,
                translations,
                property_status_id: defaultStatus,
                [realEstateGeneralFields.CONSTRUCTION_COEFFICIENT]: values?.general.construction_coefficient
                    ?.toString()
                    .replace(/\./g, '')
                    .replace(/,/g, '.'),
                [realEstateGeneralFields.PRICE]: values?.general.price?.toString().replace(/\./g, '').replace(/,/g, '.'),
                [realEstateGeneralFields.PRICE_INITIAL]: values?.general.price_initial?.toString().replace(/\./g, '').replace(/,/g, '.'),
                [realEstateGeneralFields.PRICE_CLIENT]: values?.general.price_client?.toString().replace(/\./g, '').replace(/,/g, '.'),
                [realEstateGeneralFields.PRICE_M2]: values?.general.price_m2?.toString().replace(/\./g, '').replace(/,/g, '.'),
                [realEstateGeneralFields.PRICE_OLD]: values?.general.price_old?.toString().replace(/\./g, '').replace(/,/g, '.'),
            }

            createProperty({ data: submitValues, isEdit: false })
        },
    })

    const propertyServiceId = getIn(formik.values?.general, realEstateGeneralFields.PROPERTY_SERVICE_ID)
    const propertyTypeId = getIn(formik.values?.general, realEstateGeneralFields.PROPERTY_TYPE_ID)
    const hasServiceAndType = Boolean(propertyServiceId && propertyTypeId)

    const tabs = propertyGeneralFormTabs(false, hasServiceAndType)

    const { setFieldValue } = formik
    const { sideFormValues } = useFormDataExtraction(formik?.values?.general)

    useFormValidationErrorModal(formik, [formInputs, descriptionInputs])

    // effect to preserve user values from default side form
    // TODO: split side forms from main forms
    useEffect(() => {
        const propertyServiceId = getIn(formik?.values?.general, realEstateGeneralFields.PROPERTY_SERVICE_ID)
        const propertyTypeId = getIn(formik?.values?.general, realEstateGeneralFields.PROPERTY_TYPE_ID)

        if (!propertyServiceId || !propertyTypeId) return

        const sideFormData = getValidData(sideFormValues)

        Object.entries(sideFormData).forEach(([key, value]) => {
            setFieldValue(`general.${key}`, value)
        })
    }, [sideFormValues, formik?.values?.general, setFieldValue])

    useEffect(() => {
        return () => {
            dispatch(resetFormFilters())
        }
    }, [dispatch])

    const handleTabChange = (tab: string) => {
        if (activeTab !== tab) {
            setActiveTab(tab)
        }
    }

    const renderFormButtons = () => <FormActionButtons disabled={false} hasCancel={true} cancelAction={() => history.push('/properties')} />

    return (
        <div className="page-content">
            <Container fluid>
                <FormikProvider value={formik}>
                    <Form onSubmit={(e) => formik.handleSubmit(e)}>
                        <PageTitle title={t('form.property.create.title')}>{renderFormButtons()}</PageTitle>

                        <TabContent activeTab={activeTab} className="text-muted">
                            {activeTab === 'general' && (
                                <TabPane tabId="general">
                                    <PropertyFormGeneralTab
                                        tabs={tabs}
                                        activeTab={activeTab}
                                        inputs={formInputs}
                                        formik={formik}
                                        module={location}
                                        handleTabChange={handleTabChange}
                                    >
                                        <PropertyFormGeneralTabSide />
                                        <PropertyFormGeneralTabMain />
                                    </PropertyFormGeneralTab>
                                </TabPane>
                            )}

                            {activeTab === 'description' && (
                                <TabPane tabId="description">
                                    <PropertyFormDescriptionTab
                                        subform="description"
                                        tabs={tabs}
                                        activeTab={activeTab}
                                        inputs={descriptionInputs}
                                        formik={formik}
                                        id={null}
                                        handleTabChange={handleTabChange}
                                    >
                                        <PropertyFormDescriptionTabSide />
                                        <PropertyFormDescriptionTabMain />
                                    </PropertyFormDescriptionTab>
                                </TabPane>
                            )}
                        </TabContent>
                    </Form>
                </FormikProvider>
            </Container>
        </div>
    )
}

export default PropertyCreate
